import React, { createContext, useContext, useEffect, useState } from 'react'

import { fromSdkNotificationList, NotificationItemAdapter, PaginatedItems } from '@makyoapp/makyo-common'

import { useNotificationListQuery } from '@/resolvers/user/query'
import { useNotificationsContext } from '@/context/NotificationsContext'
import { NOTIFICATIONS_ROWS_PER_PAGE } from '@/shared/constants'
import { useQueryParam } from '@/shared/makyo-hooks'

interface MyNState {
  /**
   * the notifications list
   */
  notifications: PaginatedItems<NotificationItemAdapter>
  /**
   * set notifications list
   */
  setNotifications: (nextItems: PaginatedItems<NotificationItemAdapter>) => void
  /**
   * checkbox state for current page
   */
  checks: NotificationItemAdapter[]
  /**
   * update checkbox state
   */
  setChecks: (newChecks: NotificationItemAdapter[]) => void
  /**
   * selected notif to show in modal
   */
  selectedNotif?: NotificationItemAdapter
  /**
   * set selected notif
   */
  setSelectedNotif: (notif?: NotificationItemAdapter) => void
  /**
   * is loading
   */
  isLoading: boolean
  /**
   * set is loading
   */
  setIsLoading: (next: boolean) => void
  /**
   * current page
   */
  page: number
  /**
   * set current page
   */
  setPage: (nextPage: number) => void
  /**
   * handle list all trashes
   */
  handleList: () => void
}
const MyNotificationsContext = createContext<MyNState>({
  notifications: {
    items: [],
    totalCount: 0,
  },
  setNotifications: () => null,

  checks: [],
  setChecks: () => null,

  selectedNotif: undefined,
  setSelectedNotif: () => null,

  isLoading: false,
  setIsLoading: () => null,

  page: 1,
  setPage: () => null,

  handleList: () => null,
})

export function useMyNotificationsContext() {
  return useContext(MyNotificationsContext)
}

interface MyNotificationsProviderProps {
  children?: React.ReactNode
}

/**
 * handle shared state in `UserMyNotifications`
 */
export const MyNotificationsProvider = ({ children }: MyNotificationsProviderProps) => {
  const { setNotifications: setGlobalNotif, unreadCount, setUnreadCount } = useNotificationsContext()

  const [checks, setChecks] = useState<NotificationItemAdapter[]>([])
  const [selectedNotif, setSelectedNotif] = useState<NotificationItemAdapter | undefined>(undefined)
  const [notifications, setNotifications] = useState<PaginatedItems<NotificationItemAdapter>>({
    items: [],
    totalCount: 0,
  })

  const { page, isLoading, setIsLoading, setPage } = useQueryParam()
  const { data, refetch: handleList, isFetching, isLoading: queryLoading } = useNotificationListQuery({ page })

  useEffect(() => {
    if (data) {
      const fromSdk = fromSdkNotificationList(data._Data)
      setNotifications({
        items: fromSdk,
        totalCount: data._Pages * NOTIFICATIONS_ROWS_PER_PAGE,
      })
      if (page === 1) {
        setGlobalNotif(fromSdk)
      }
      if (unreadCount !== data._NotReadedCount) {
        setUnreadCount(data._NotReadedCount)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, page])

  return (
    <MyNotificationsContext.Provider
      value={{
        notifications,
        setNotifications,
        checks,
        setChecks,
        selectedNotif,
        setSelectedNotif,
        isLoading: isLoading || isFetching || queryLoading,
        setIsLoading,
        page,
        setPage,
        handleList,
      }}
    >
      {children}
    </MyNotificationsContext.Provider>
  )
}
